import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const usePopover = ({ disabled = false }: { disabled: boolean }) => {
  const [isOpen, setIsOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const open = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
  }, [disabled]);

  const close = useCallback(() => {
    if (disabled) return;
    setIsOpen(false);
  }, [disabled]);

  const toggleHandler = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  }, [disabled]);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDownCapture = (event: PointerEvent) => {
      const { target } = event;
      if (!target || !(target instanceof Node)) return;

      const isInsideWrapper = wrapperRef.current?.contains(target) ?? false;
      const isInsideContent = contentRef.current?.contains(target) ?? false;

      if (!isInsideWrapper && !isInsideContent) {
        close();
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('pointerdown', onPointerDownCapture, true);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDownCapture, true);
      document.removeEventListener('keydown', onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, disabled]);

  return useMemo(
    () => ({
      isOpen,
      toggleHandler,
      wrapperRef,
      contentRef,
      open,
      close,
    }),
    [isOpen, toggleHandler, open, close]
  );
};
